import { THeachAIAuthProvider } from 'helpers/auth';
import {
    useState,
    createContext,
    useContext,
    useEffect
} from 'react';
import apiClient, { setAuthTokenProvider } from 'helpers/apiService';
import { v4 as uuidv4 } from 'uuid';
import axios from "axios";

// https://stackoverflow.com/questions/66001643/how-to-properly-store-access-token-in-memory-react
// https://javascript.plainenglish.io/how-to-secure-jwt-in-a-single-page-application-6a46e69fc393

// Define the type for allowed scopes and initialize the enabled features for each scope
type Scope = 'sidebar' | 'dashboard' | 'meetings';

// Define the shape of user object
interface UserAuth {
    user_id: number;
    full_name: string;
    first_name: string;
    last_name: string;
    role: "user" | "admin" | "super-admin";
    customer_name: string;
    customer_id: number;
    //login: string;
    //access_token: string;
    //refresh_token: string;
    picture: string;
    is_coach: boolean;
    is_mentor: boolean;
    login_at: Date;
    // Add any other relevant user properties
}
interface AuthContextType {
    user: UserAuth | null;
    accessToken: string | null;
    setAccessToken: (access_token: string) => void;
    isAuthenticated: boolean;
    setUser: (user: UserAuth | null) => void;
    signin: (user: string, password: string, rememberMe: boolean, callback: (destination: string, error: string | null) => void) => void;
    autoSignin: (callback: (destination: string, error: string | null) => void) => void;
    signout: (callback: VoidFunction) => void;
    checkFeatureAccess: (featureName: string, scope: Scope) => boolean;

}

const AuthContext = createContext<AuthContextType | undefined>(null!);

function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<UserAuth | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    const isAuthenticated = !!user; // Check if user exists

    // An object to map each scope to its enabled features
    const [enabledFeatures, setEnabledFeatures] = useState<Record<Scope, string[]>>();
    /*
    const enabledFeatures: Record<Scope, string[]> = {
        sidebar: ['feature1', 'feature2', 'feature3'],  // Example features for sidebar
        dashboard: ['feature4', 'feature5', 'feature6'], // Example features for dashboard
        meetings: ['feature7', 'feature8', 'feature9']   // Example features for meetings
    };
    */

    const finishSigninFlow = async (access_token: string) => {

        const uuid = uuidv4();
        /*
            Persistence: Data stored in sessionStorage is temporary and is only available for the duration of the page session. 
            It is cleared when the page session ends, which typically happens when the browser or tab is closed.
        */
        sessionStorage.setItem('clientId', uuid);
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND}/auth/me`, {},
            {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${access_token}`
                }
            }
        );

        setAccessToken(access_token);
        const loggedInUser: UserAuth = {
            //login: login,
            customer_id: data.customer_id,
            customer_name: data.customer_name,
            full_name: data.full_name,
            user_id: data.user_id,
            role: data.role,
            //access_token: access_token,
            //refresh_token: data.refresh_token,
            picture: data.picture,
            is_coach: data.is_coach === 1,
            is_mentor: data.is_mentor === 1,
            first_name: data.first_name,
            last_name: data.last_name,
            login_at: new Date(data.login_at)
            // Add other properties as needed
            // TODO add timezone
            // TODO a
        };
        setUser(loggedInUser);
        await loadEnabledFeature(access_token);
        return loggedInUser;
    }

    const signin = async (login: string, password: string, rememberMe: boolean,
        callback: (destination: string, error: string | null) => void) => {

        const handleSuccess = async (access_token: string) => {
            try {
                const loggedInUser: UserAuth = await finishSigninFlow(access_token);
                localStorage.setItem('remember_me', rememberMe ? "true" : "false");
                if (loggedInUser.customer_id == 0)
                    callback('/customer/selector', null);
                else
                    callback('/dashboard/home', null);
            } catch (error) {
                console.error("Login failed:", error);
                callback("/", '');
            }

        }

        const handleLoginError = async (error: string) => {
            callback("/", error);
        }

        return await THeachAIAuthProvider.signin(login, password, handleSuccess, handleLoginError);
    };

    const autoSignin = async (callback: (destination: string, error: string | null) => void) => {

        const handleSuccess = async (access_token: string) => {
            const loggedInUser: UserAuth = await finishSigninFlow(access_token);
            if (loggedInUser.customer_id == 0)
                callback('/customer/selector', null);
            else
                callback('/dashboard/home', null);
        }

        try {
            const { data } = await axios.post(`${process.env.REACT_APP_BACKEND}/auth/refresh`, {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true // ✅ REQUIRED for cookies
                }
            );

            setAccessToken(data.access_token);
            handleSuccess(data.access_token);
        } catch (error) {
            console.error("Failed to refresh token", error);
        }

    };

    const signout = async (callback: VoidFunction) => {
        return THeachAIAuthProvider.signout(async () => {
            console.log("THeachAIAuthProvider callback signout");
            setUser(null);
            sessionStorage.removeItem('clientId');
            localStorage.removeItem('remember_me');

            await axios.post(`${process.env.REACT_APP_BACKEND}/auth/logout`, {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true // ✅ REQUIRED for cookies
                }
            );
            callback();
        });
    };


    // **Refresh Token Function**
    const refreshAccessToken = async () => {
        try {
            //const { data } = await apiClient.post(`${process.env.REACT_APP_BACKEND}/auth/refresh`, {});
            const { data } = await axios.post(`${process.env.REACT_APP_BACKEND}/auth/refresh`, {},
                {
                    headers: {
                        //Authorization: 'Bearer ' + accessToken,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true // ✅ REQUIRED for cookies
                });

            setAccessToken(data.access_token);
        } catch (error) {
            console.error("Failed to refresh token", error);
            signout(() => { });
        }
    };

    const loadEnabledFeature = async (access_token: string) => {
        const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND}/auth/enabled-features`,
            {
                headers: {
                    'accept': 'application/json',
                    'Authorization': 'Bearer ' + access_token
                },
                withCredentials: true,
            }
        );
        setEnabledFeatures(data);
    };

    const checkFeatureAccess = (featureName: string, scope: Scope) => {

        if (enabledFeatures === null || enabledFeatures === undefined) return false;

        const enabled = enabledFeatures[scope]?.includes(featureName.toLocaleLowerCase()) ?? false;

        // Check if the given scope has the feature
        if (enabled === false)
            console.log(scope + " - " + featureName.toLocaleLowerCase() + ":" + enabled);
        return enabled;
    }

    // **Set token provider for apiClient**
    useEffect(() => {
        setAuthTokenProvider(() => accessToken, refreshAccessToken); // Pass a function to get the latest access token
    }, [accessToken]); // Update apiClient when accessToken changes

    const value = { user, accessToken, setAccessToken, isAuthenticated, setUser, signin, signout, checkFeatureAccess, autoSignin };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = (): AuthContextType => {
    /**
     * Usages:
     * 1) import { useAuth } from './AuthProvider';
     * 2) const { user, isAuthenticated, login, logout } = useAuth();
     */
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * the AuthProvider component is not exported as the default export because 
 * it's often preferable to have named exports for better clarity and maintainability, 
 * especially in larger codebases or when you have multiple exports from a file.
 * Named exports explicitly state what is being exported from a file, 
 * making it clear to other developers which components, functions, or objects are available for use.
 * 
 * Usage:
 *  import { AuthProvider, useAuth } from 'providers/AuthProvider'
 */
export { AuthProvider, useAuth };